import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import type { UploadFormat } from '@tabservice/tabfile-client';
import { fileMatchesFormat, formatMeta } from './formats';

export interface TablePreview {
  kind: 'table';
  columns: string[];
  rows: string[][];
  /** True when the file has more rows than we show here. */
  truncated: boolean;
}

export interface TextPreview {
  kind: 'text';
  lines: string[];
  truncated: boolean;
}

export interface XmlPreview {
  kind: 'xml';
  rootElement: string | null;
  sizeBytes: number;
}

export type FilePreview = TablePreview | TextPreview | XmlPreview;

const PREVIEW_ROWS = 20;
const PREVIEW_LINES = 5;
const HEAD_BYTES = 16 * 1024;

export function contentTypeFor(format: UploadFormat): FormatContentType {
  return formatMeta[format].contentType;
}

type FormatContentType = (typeof formatMeta)[UploadFormat]['contentType'];

export function fileFitsFormat(file: File, format: UploadFormat): boolean {
  return fileMatchesFormat(file.name, format);
}

function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

/**
 * Reads enough of the file to show the customer what we are about to send.
 * Row and column files get a table, 1220 files get their first lines, XML gets
 * its root element.
 */
export async function previewFile(file: File, format: UploadFormat): Promise<FilePreview> {
  if (format === 'csv') return previewCsv(file);
  if (format === 'xlsx') return previewWorkbook(file);
  if (format === 'pub1220') return previewLines(file);
  return previewXml(file);
}

function previewCsv(file: File): Promise<TablePreview> {
  return new Promise((resolve, reject) => {
    Papa.parse<string[]>(file, {
      preview: PREVIEW_ROWS + 1,
      skipEmptyLines: 'greedy',
      complete: (result) => {
        const rows = result.data.filter((row) => Array.isArray(row) && row.some((cell) => toText(cell) !== ''));
        const [header, ...body] = rows;
        resolve({
          kind: 'table',
          columns: (header ?? []).map(toText),
          rows: body.slice(0, PREVIEW_ROWS).map((row) => row.map(toText)),
          truncated: body.length > PREVIEW_ROWS,
        });
      },
      error: (error: Error) => reject(new Error(`Could not read that CSV: ${error.message}`)),
    });
  });
}

async function previewWorkbook(file: File): Promise<TablePreview> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', sheetRows: PREVIEW_ROWS + 1, cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) throw new Error('That workbook has no sheets in it.');
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error('That workbook has no sheets in it.');
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, blankrows: false, defval: '' });
  const [header, ...body] = grid;
  return {
    kind: 'table',
    columns: (header ?? []).map(toText),
    rows: body.slice(0, PREVIEW_ROWS).map((row) => row.map(toText)),
    truncated: body.length > PREVIEW_ROWS,
  };
}

async function readHead(file: File): Promise<string> {
  const slice = file.slice(0, Math.min(file.size, HEAD_BYTES));
  return slice.text();
}

async function previewLines(file: File): Promise<TextPreview> {
  const head = await readHead(file);
  const lines = head.split(/\r?\n/).filter((line) => line.trim() !== '');
  return {
    kind: 'text',
    lines: lines.slice(0, PREVIEW_LINES),
    truncated: lines.length > PREVIEW_LINES || file.size > HEAD_BYTES,
  };
}

/** Finds the first real element, skipping the declaration, comments and doctype. */
export function findRootElement(xml: string): string | null {
  const withoutNoise = xml
    .replace(/<\?[\s\S]*?\?>/g, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<!DOCTYPE[\s\S]*?>/gi, '');
  const match = /<\s*([A-Za-z_][\w.:-]*)/.exec(withoutNoise);
  return match ? (match[1] ?? null) : null;
}

async function previewXml(file: File): Promise<XmlPreview> {
  const head = await readHead(file);
  return { kind: 'xml', rootElement: findRootElement(head), sizeBytes: file.size };
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/** PUTs the file to the link Tab Service gave us, reporting progress as it goes. */
export function putFile(
  url: string,
  file: File,
  contentType: string,
  onProgress: (progress: UploadProgress) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url, true);
    // Azure Blob Storage refuses a PUT that does not say what kind of blob it is.
    request.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    // Always text/plain on the wire. Browsers let that value through without asking the
    // storage account's CORS rules about it; text/xml or text/csv would need the account
    // to list Content-Type as an allowed header, and a shared account may not. The real
    // type travels in the upload record, and Tab Service reads the file by its format.
    void contentType;
    request.setRequestHeader('Content-Type', 'text/plain');
    request.upload.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.round((event.loaded / event.total) * 100),
      });
    };
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress({ loaded: file.size, total: file.size, percent: 100 });
        resolve();
      } else {
        // The storage service answers with XML meant for developers. Keep it in the console
        // and give the person a sentence they can act on.
        console.error('upload refused by storage', request.status, request.responseText);
        reject(
          new Error(
            request.status === 403
              ? 'The upload link has expired. Go back and choose the file again.'
              : 'The file could not be stored. Try again, and contact Tab Service if it continues.',
          ),
        );
      }
    };
    request.onerror = () => reject(new Error('The upload could not reach the storage service.'));
    request.send(file);
  });
}
